import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Plus, Pencil, Trash2, Loader2, ArrowUpDown, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DndContext, closestCenter, SortableContext, arrayMove,
  verticalListSortingStrategy, SortableItem, useDndSensors,
} from "../components/Sortable";
import { apiUploadImage } from "../adminApi";

// Campo immagine con upload (Cloudinary), ottimizzazione automatica e anteprima.
function ImageUploadField({ label, value, onChange, folder, context, hint }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await apiUploadImage(file, folder, undefined, context);
      onChange(res.url); // URL grezzo: l'ottimizzazione 1:1 viene applicata dal sito
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-line bg-surface3">
          {value ? (
            <>
              <img src={value} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => onChange("")}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-ink/80 text-chalk hover:bg-ink"
                aria-label="Rimuovi immagine"
                data-testid="taxonomy-image-remove"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-tertiary">
              <Upload className="h-5 w-5" />
            </div>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="taxonomy-image-input" />
          <Button type="button" variant="secondary" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="taxonomy-image-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Caricamento…" : value ? "Sostituisci foto" : "Carica foto"}
          </Button>
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="oppure incolla un URL…" className="h-8 text-xs" />
          {hint && <p className="text-[11px] text-steel">{hint}</p>}
        </div>
      </div>
    </div>
  );
}


/**
 * Pagina tassonomia generica riusabile per Categorie e Marchi.
 */
export default function TaxonomyPage({
  title, subtitle, nameLabel, nameField,
  api, testidPrefix, hideHeader = false,
}) {
  const sensors = useDndSensors();
  const [items, setItems] = useState(null);
  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const [delOpen, setDelOpen] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [moveTo, setMoveTo] = useState("");

  // Ordina prodotti
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderItem, setOrderItem] = useState(null);
  const [orderProducts, setOrderProducts] = useState(null);
  const [savingProdOrder, setSavingProdOrder] = useState(false);

  const openOrder = async (item) => {
    setOrderItem(item); setOrderProducts(null); setOrderOpen(true);
    try { setOrderProducts(await api.products(item.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const onProdDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oi = orderProducts.findIndex((p) => p.id === active.id);
    const ni = orderProducts.findIndex((p) => p.id === over.id);
    setOrderProducts(arrayMove(orderProducts, oi, ni));
  };
  const saveProdOrder = async () => {
    setSavingProdOrder(true);
    try { await api.setOrder(orderItem.id, orderProducts.map((p) => p.id)); toast.success("Ordine prodotti salvato"); setOrderOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSavingProdOrder(false); }
  };

  const load = () => { api.list().then((d) => { setItems(d); setOrderChanged(false); }).catch((e) => toast.error(e?.response?.data?.detail || "Errore di caricamento")); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    setItems(arrayMove(items, oldIndex, newIndex));
    setOrderChanged(true);
  };
  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.reorder(items.map((i) => i.id));
      toast.success("Ordine salvato");
      setOrderChanged(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSavingOrder(false); }
  };

  const openCreate = () => { setEditing(null); setForm({ visible: true }); setEditOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setEditOpen(true); };

  const submit = async () => {
    const name = (form[nameField] || "").trim();
    if (!name) { toast.error(`${nameLabel} obbligatorio.`); return; }
    try {
      if (editing) await api.update(editing.id, form);
      else await api.create(form);
      toast.success("Salvato");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore di salvataggio"); }
  };

  const askDelete = (item) => { setDelItem(item); setMoveTo(""); setDelOpen(true); };
  const confirmDelete = async () => {
    try {
      await api.remove(delItem.id, moveTo || undefined);
      toast.success("Eliminato");
      setDelOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const others = (items || []).filter((i) => delItem && i.id !== delItem.id);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {!hideHeader && <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>}
          {!hideHeader && <p className="mt-1 text-sm text-steel">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {orderChanged && (
            <Button variant="secondary" size="sm" onClick={saveOrder} disabled={savingOrder} data-testid="taxonomy-save-order-button">
              {savingOrder && <Loader2 className="h-4 w-4 animate-spin" />} Salva ordine
            </Button>
          )}
          <Button variant="lime" size="sm" onClick={openCreate} data-testid="taxonomy-create-button"><Plus className="h-4 w-4" /> Nuovo</Button>
        </div>
      </div>

      <Card className="border-line bg-surface2">
        <CardContent className="p-3">
          {!items ? (
            <div className="flex flex-col gap-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-steel">Ancora nessun elemento.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-2" data-testid="taxonomy-reorder-list">
                  {items.map((item) => (
                    <SortableItem key={item.id} id={item.id}>
                      {({ setNodeRef, style, attributes, listeners }) => (
                        <div ref={setNodeRef} style={style} {...attributes} className="flex items-center gap-3 rounded-lg border border-line bg-surface3 px-3 py-2.5">
                          <button className="cursor-grab text-tertiary" {...listeners} aria-label="Trascina"><GripVertical className="h-4 w-4" /></button>
                          {(item.image || item.logo) && (
                            <img src={item.image || item.logo} alt="" className="h-9 w-9 shrink-0 rounded-md object-cover" />
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-chalk">{item[nameField]}</span>
                            <span className="text-xs text-steel">{item.product_count} prodotti{item.visible === false ? " · nascosto" : ""}</span>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openOrder(item)} aria-label="Ordina prodotti" data-testid="taxonomy-order-products"><ArrowUpDown className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)} aria-label="Modifica"><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => askDelete(item)} aria-label="Elimina"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      )}
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifica" : "Nuovo"} {title.toLowerCase().slice(0, -1)}</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>{nameLabel}</Label>
              <Input value={form[nameField] || ""} onChange={(e) => setForm({ ...form, [nameField]: e.target.value })} data-testid="taxonomy-name-input" autoFocus />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Descrizione</Label>
              <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <ImageUploadField
              label={testidPrefix === "brand" ? "Logo" : "Immagine categoria"}
              value={form[testidPrefix === "brand" ? "logo" : "image"] || ""}
              onChange={(v) => setForm({ ...form, [testidPrefix === "brand" ? "logo" : "image"]: v })}
              folder={testidPrefix === "brand" ? "rinova/brands" : "rinova/categories"}
              context={testidPrefix === "brand" ? "logo" : "card"}
              hint={testidPrefix === "brand" ? "Logo ottimizzato automaticamente." : "Ottimizzata e ritagliata 1:1 automaticamente sul sito."}
            />
            <div className="flex items-center justify-between">
              <Label>Visibile sul sito</Label>
              <Switch checked={form.visible !== false} onCheckedChange={(c) => setForm({ ...form, visible: c })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button variant="lime" onClick={submit} data-testid="taxonomy-save-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ordina prodotti */}
      <Sheet open={orderOpen} onOpenChange={setOrderOpen}>
        <SheetContent side="right" className="w-full border-line bg-surface sm:max-w-md">
          <SheetHeader><SheetTitle>Ordina prodotti — {orderItem?.[nameField]}</SheetTitle></SheetHeader>
          <p className="mt-1 text-xs text-steel">Trascina per ordinare. I prodotti in evidenza restano sempre in alto sul sito.</p>
          <div className="mt-4 max-h-[70vh] overflow-y-auto">
            {!orderProducts ? (
              <div className="flex flex-col gap-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-11 rounded-lg" />)}</div>
            ) : orderProducts.length === 0 ? (
              <p className="py-6 text-sm text-steel">Nessun prodotto in questa voce.</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onProdDragEnd}>
                <SortableContext items={orderProducts.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                  <div className="flex flex-col gap-2" data-testid="taxonomy-product-order-list">
                    {orderProducts.map((p) => (
                      <SortableItem key={p.id} id={p.id}>
                        {({ setNodeRef, style, attributes, listeners }) => (
                          <div ref={setNodeRef} style={style} {...attributes} className="flex items-center gap-3 rounded-lg border border-line bg-surface3 px-3 py-2">
                            <button className="cursor-grab text-tertiary" {...listeners} aria-label="Trascina"><GripVertical className="h-4 w-4" /></button>
                            <span className="min-w-0 flex-1 truncate text-sm text-chalk">{p.name}</span>
                            {p.featured && <span className="rounded-full bg-lime/15 px-2 py-0.5 text-[10px] font-medium text-lime">In evidenza</span>}
                          </div>
                        )}
                      </SortableItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
          <SheetFooter className="mt-4">
            <Button variant="lime" onClick={saveProdOrder} disabled={savingProdOrder} data-testid="taxonomy-save-product-order">Salva ordine</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Eliminare «{delItem?.[nameField]}»?</DialogTitle></DialogHeader>
          {delItem?.product_count > 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-steel">Contiene {delItem.product_count} prodotti. Dove vuoi spostarli?</p>
              <Select value={moveTo} onValueChange={setMoveTo}>
                <SelectTrigger data-testid="taxonomy-move-select"><SelectValue placeholder="Scegli destinazione" /></SelectTrigger>
                <SelectContent>
                  {others.map((o) => <SelectItem key={o.id} value={o.id}>{o[nameField]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-sm text-steel">L'operazione non può essere annullata.</p>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDelOpen(false)}>Annulla</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={delItem?.product_count > 0 && !moveTo} data-testid="taxonomy-confirm-delete">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
